import { LitElement, css, html, PropertyValues } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';

// Next node up the flat tree: into the slot a node is assigned to, and out of shadow roots to their host.
const flatTreeParent = (node: Node): Node | null => {
  if (node instanceof ShadowRoot) return node.host;
  if (node instanceof Element && node.assignedSlot) return node.assignedSlot;
  return node.parentNode;
};

/** A warning icon button that reveals a message (the default slot, which may contain links) on hover,
 * keyboard focus or activation. The message stays open while the pointer or focus is anywhere on the
 * icon or the message, closes with Escape from anywhere on the page, and opens upwards when there
 * isn't room below it inside the nearest scrolling/clipping ancestor.
 *
 * The trigger lives in this element's shadow root but the slotted message lives in the consumer's
 * tree, so every "is this inside me?" check has to cover both — `this.contains()` for the slotted
 * content and `this.renderRoot.contains()` for the trigger. */
@customElement('ns-warning-tip')
export class NSWarningTip extends LitElement {
  /** Accessible name of the icon button, e.g. "Companies House details mismatch". The slotted message
   * is announced as its description, so the label doesn't need to repeat it. */
  @property({ type: String })
  label = '';

  @state()
  private __open = false;

  @query('.trigger')
  private __trigger?: HTMLButtonElement;

  @query('.text')
  private __text?: HTMLElement;

  // Only set by real pointer movement over the warning, so text that pops up under a resting cursor
  // (opened by keyboard focus) doesn't count as hovered.
  private __pointerInside = false;

  // Scrolling/clipping ancestors watched while open, so scrolling them or resizing the window re-runs
  // __place(). Scroll events don't cross shadow roots, so a window listener wouldn't see e.g. the card
  // body scrolling.
  private __scrollParents: Element[] = [];

  private readonly __placeOnScroll = () => this.__place();

  // Listens on the document while open, so Escape dismisses it even when it was opened by hover and
  // keyboard focus is somewhere else on the page. An Escape another widget has already handled (e.g. an
  // open dropdown closing itself) is left alone, so one press only dismisses one thing.
  private readonly __dismissOnEscape = (e: KeyboardEvent) => {
    if (e.key !== 'Escape' || e.defaultPrevented || !this.__open) return;
    // Focus the trigger before closing if slotted content (the link) has focus: hiding the text would
    // otherwise drop focus to <body>, and closing first would let focusin re-open it as focus moves.
    if (this.__slottedFocus()) this.__trigger?.focus();
    this.__open = false;
  };

  static styles = css`
    :host {
      display: inline-flex;
      position: relative;
      z-index: 2;
    }
    :host([hidden]) {
      display: none;
    }

    .wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }

    .trigger {
      /* Above the text, so the edge of the focus ring nearest the text isn't painted over by it */
      position: relative;
      z-index: 1;
      display: inline-flex;
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
    }
    .trigger:focus-visible {
      outline: 2px solid var(--quaternary-text-color);
      outline-offset: 2px;
    }

    .text {
      position: absolute;
      top: 20px;
      right: 0;
      box-sizing: border-box;
      background: var(--primary-warning-background-color);
      color: var(--ns-theme-black);
      width: 200px;
      padding: var(--wa-space-xs);
      border-radius: var(--wa-border-radius-l);
      overflow-wrap: anywhere;
    }
    .text[hidden] {
      display: none;
    }

    /* Invisible hover bridge from the icon to the text, so diagonal pointer moves stay inside the wrapper */
    .text::before {
      content: '';
      position: absolute;
      z-index: -1;
      left: 0;
      right: 0;
      top: -20px;
      height: 36px;
    }

    /* Set by __place() when there isn't room below; the bridge flips with it */
    .text[data-placement='above'] {
      top: auto;
      bottom: 20px;
    }
    .text[data-placement='above']::before {
      top: auto;
      bottom: -20px;
    }

    ::slotted(a) {
      color: inherit;
    }

    .visually-hidden {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }
  `;

  updated(changed: PropertyValues) {
    if (!changed.has('__open')) return;
    if (this.__open) {
      this.__place();
      document.addEventListener('keydown', this.__dismissOnEscape);
    } else {
      document.removeEventListener('keydown', this.__dismissOnEscape);
    }
    this.__watchScroll(this.__open);
  }

  disconnectedCallback() {
    // A detached warning (e.g. its card collapsed back into the feed) shouldn't stay open or leave its
    // document/scroll listeners behind.
    this.__open = false;
    this.__pointerInside = false;
    document.removeEventListener('keydown', this.__dismissOnEscape);
    this.__watchScroll(false);
    super.disconnectedCallback();
  }

  // The trigger (shadow root) or slotted content (light DOM).
  private __contains(node: Node | null) {
    return !!node && (this.contains(node) || this.renderRoot.contains(node));
  }

  // Focus anywhere in this element: the root's activeElement is retargeted to this host while the
  // trigger has focus, and is the slotted element itself while that has focus.
  private __hasFocus() {
    return this.__contains((this.getRootNode() as Document | ShadowRoot).activeElement);
  }

  private __slottedFocus() {
    const active = (this.getRootNode() as Document | ShadowRoot).activeElement;
    return active !== this && this.__contains(active);
  }

  // Opens the text upwards when it would be cut off below (by a scroll area or the viewport) and
  // there's more room above; stays below on a tie, matching the default CSS.
  private __place() {
    const text = this.__text;
    const trigger = this.__trigger;
    if (!text || !trigger) return;

    const { top, bottom } = this.__visibleArea(trigger);
    const triggerRect = trigger.getBoundingClientRect();
    const roomBelow = bottom - triggerRect.bottom;
    const roomAbove = triggerRect.top - top;
    const fitsBelow = text.getBoundingClientRect().height <= roomBelow;
    text.dataset.placement = !fitsBelow && roomAbove > roomBelow ? 'above' : 'below';
  }

  private __watchScroll(watch: boolean) {
    this.__scrollParents.forEach((el) => el.removeEventListener('scroll', this.__placeOnScroll));
    window.removeEventListener('resize', this.__placeOnScroll);
    this.__scrollParents = watch && this.__trigger ? this.__clippingAncestors(this.__trigger) : [];
    this.__scrollParents.forEach((el) => el.addEventListener('scroll', this.__placeOnScroll, { passive: true }));
    if (watch) window.addEventListener('resize', this.__placeOnScroll);
  }

  // Every scrolling or clipping ancestor of `el` in the flat tree.
  private __clippingAncestors(el: Element) {
    const found: Element[] = [];
    for (let node = flatTreeParent(el); node; node = flatTreeParent(node)) {
      if (node instanceof Element && getComputedStyle(node).overflowY !== 'visible') found.push(node);
    }
    return found;
  }

  // Vertical bounds of what's actually visible around `el`: the viewport, narrowed by every clipping ancestor.
  private __visibleArea(el: Element) {
    let top = 0;
    let bottom = window.innerHeight;
    for (const node of this.__clippingAncestors(el)) {
      const rect = node.getBoundingClientRect();
      top = Math.max(top, rect.top);
      bottom = Math.min(bottom, rect.bottom);
    }
    return { top, bottom };
  }

  private __onPointerMove(e: PointerEvent) {
    if (e.pointerType !== 'touch') this.__pointerInside = true;
  }

  private __onMouseLeave() {
    this.__pointerInside = false;
    if (this.__hasFocus()) return;
    this.__open = false;
  }

  private __onFocusOut(e: FocusEvent) {
    if (this.__contains(e.relatedTarget as Node | null) || this.__pointerInside) return;
    if (e.relatedTarget === null && !document.hasFocus()) {
      // The window itself lost focus (app switch, or a mailto link opening a mail client). Hiding the
      // text now would drop focus from the link to <body>, so re-check once the window is focused again.
      window.addEventListener(
        'focus',
        () => {
          if (!this.__hasFocus() && !this.__pointerInside) this.__open = false;
        },
        { once: true },
      );
      return;
    }
    this.__open = false;
  }

  // The trigger's click only opens, never toggles: a mouse click or tap has already opened it through
  // hover/focus, so toggling would close it again straight away.
  render() {
    return html`
      <div
        class="wrapper"
        @mouseenter="${() => (this.__open = true)}"
        @mouseleave="${this.__onMouseLeave}"
        @pointermove="${this.__onPointerMove}"
        @focusin="${() => (this.__open = true)}"
        @focusout="${this.__onFocusOut}"
      >
        <button
          type="button"
          class="trigger"
          aria-expanded="${this.__open ? 'true' : 'false'}"
          aria-controls="text"
          aria-describedby="text"
          @click="${() => (this.__open = true)}"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="20px"
            viewBox="0 0 24 24"
            width="20px"
            fill="#ec4a5d"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M0 0h24v24H0z" fill="none" />
            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
          </svg>
          <span class="visually-hidden">${this.label}</span>
        </button>
        <div class="text" id="text" ?hidden="${!this.__open}"><slot></slot></div>
      </div>
    `;
  }
}
